import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MapPin, Globe, ShieldCheck, HardDrive } from 'lucide-react';
import { Link } from 'react-router-dom';

gsap.registerPlugin(ScrollTrigger);

const cities = [
    { name: "Dallas, TX", id: "dallas-tx" },
    { name: "Miami, FL", id: "miami-fl" },
    { name: "Denver, CO", id: "denver-co" },
    { name: "Atlanta, GA", id: "atlanta-ga" },
    { name: "Chicago, IL", id: "chicago-il" },
    { name: "Charlotte, NC", id: "charlotte-nc" },
    { name: "Austin, TX", id: "austin-tx" },
    { name: "Phoenix, AZ", id: "phoenix-az" }
];

const LocationsPage = () => {
    const mapRef = useRef(null);
    const citiesRef = useRef(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.fromTo('.loc-reveal',
                { y: 30, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.8,
                    stagger: 0.1,
                    ease: 'power2.out'
                }
            );

            gsap.fromTo('.city-pill',
                { y: 20, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.5,
                    stagger: 0.05,
                    ease: 'back.out(1.7)',
                    scrollTrigger: {
                        trigger: citiesRef.current,
                        start: 'top 90%',
                    }
                }
            );
        });
        return () => ctx.revert();
    }, []);

    return (
        <div className="pt-32 pb-24 bg-[#0A0A14] text-[#F0EFF4] min-h-screen selection:bg-[#7B61FF] selection:text-[#F0EFF4]">

            <section className="max-w-6xl mx-auto px-6 mb-24">
                <div className="text-center mb-16 max-w-3xl mx-auto loc-reveal">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#18181B] border border-[#F0EFF4]/10 text-xs font-semibold tracking-wider text-[#7B61FF] mb-6">
                        <MapPin className="w-4 h-4" />
                        GLOBAL DEPLOYMENT
                    </div>
                    <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">Shipped from the secure lab.<br /><span className="font-drama italic text-[#7B61FF] font-normal tracking-normal text-5xl md:text-7xl">Deployed anywhere.</span></h1>
                    <p className="text-lg text-[#F0EFF4]/60">
                        Stop suffering through cloud latency and remote IT tickets. We configure the hardware in our closed-network facility, ship it overnight, and deploy a true perimeter defense around your data—wherever you operate.
                    </p>
                </div>

                <div ref={mapRef} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-24 bg-[#18181B] p-8 md:p-12 rounded-[3rem] border border-[#F0EFF4]/10 loc-reveal">
                    <div>
                        <h3 className="text-2xl font-bold mb-4">The Deployment Protocol</h3>
                        <p className="text-[#F0EFF4]/70 mb-8 leading-relaxed">
                            Upon reserve payment, the Apple Silicon hardware is acquired. It takes 1 full day for our engineers to format the drive, strip OS-level telemetry, and install the encrypted Tailscale-secured OpenClaw stack.
                            <br /><br />
                            It is then sealed and shipped via priority courier. When it arrives at your facility—whether in Miami or Denver—it requires only power and an ethernet connection. The network handshake happens automatically.
                        </p>

                        <div className="space-y-4">
                            <div className="flex items-center gap-4 text-sm font-data tracking-wider bg-[#0A0A14] p-4 rounded-xl border border-[#F0EFF4]/5">
                                <ShieldCheck className="w-5 h-5 text-[#10B981]" />
                                <span>END-TO-END HARDWARE ENCRYPTION</span>
                            </div>
                            <div className="flex items-center gap-4 text-sm font-data tracking-wider bg-[#0A0A14] p-4 rounded-xl border border-[#F0EFF4]/5">
                                <Globe className="w-5 h-5 text-[#7B61FF]" />
                                <span>TAILSCALE MESH ROUTING PRE-CONFIGURED</span>
                            </div>
                        </div>
                    </div>

                    <div className="relative h-full min-h-[300px] flex items-center justify-center border border-[#F0EFF4]/10 rounded-[2rem] bg-[#0A0A14] overflow-hidden group shadow-[inset_0_0_50px_rgba(123,97,255,0.05)]">
                        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2220%22 height=%2220%22 viewBox=%220 0 20 20%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Ccircle cx=%222%22 cy=%222%22 r=%221%22 fill=%22%237b61ff%22 fill-opacity=%220.1%22/%3E%3C/svg%3E')] [mask-image:linear-gradient(to_bottom_right,white,transparent)]"></div>

                        {/* Radar sweep */}
                        <div className="absolute top-1/2 left-1/2 w-[150%] h-[150%] -translate-x-1/2 -translate-y-1/2 bg-[conic-gradient(from_0deg,transparent_0deg,rgba(123,97,255,0.1)_360deg)] animate-[spin_4s_linear_infinite] rounded-full"></div>
                        {/* Pulses */}
                        <div className="absolute w-16 h-16 rounded-full border border-[#7B61FF]/30 animate-ping"></div>
                        <div className="absolute w-32 h-32 rounded-full border border-[#7B61FF]/10 animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite]"></div>

                        <HardDrive className="w-16 h-16 text-[#7B61FF]/50 relative z-10 group-hover:text-[#7B61FF] transition-colors duration-700" />
                    </div>
                </div>

                <div className="text-center loc-reveal">
                    <h3 className="text-xl font-bold mb-8">Active Nodes in Heavy Storm Markets</h3>
                    <div ref={citiesRef} className="flex flex-wrap justify-center gap-3">
                        {cities.map((city, i) => (
                            <Link to={`/locations/${city.id}`} key={i} className="city-pill px-5 py-2 rounded-full bg-[#18181B] border border-[#F0EFF4]/10 text-sm font-medium hover:border-[#7B61FF] hover:bg-[#7B61FF] hover:text-[#0A0A14] transition-all cursor-pointer">
                                {city.name}
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <section className="text-center loc-reveal mt-20">
                <Link to="/#reserve" className="inline-flex items-center justify-center px-10 py-5 rounded-full bg-[#7B61FF] text-[#F0EFF4] font-bold tracking-wide hover:-translate-y-1 hover:scale-[1.03] transition-all duration-500 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] shadow-[0_0_30px_rgba(123,97,255,0.4)] hover:shadow-[0_0_50px_rgba(123,97,255,0.6)]">
                    Secure Shipping & Build Slot
                </Link>
            </section>

        </div>
    );
};

export default LocationsPage;
